import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';

/** Show this recipe to friends, read-only; they copy it onto their own machines (doc 30). */
export function SharePanel({ recipeId }: { recipeId: string }) {
  const queryClient = useQueryClient();
  const key = ['recipe-shares', recipeId];
  const shares = useQuery({ queryKey: key, queryFn: () => api.listShares(recipeId) });
  const [username, setUsername] = useState('');
  const done = () => queryClient.invalidateQueries({ queryKey: key });
  const add = useMutation({ mutationFn: () => api.shareRecipe(recipeId, username.trim()), onSuccess: async () => { setUsername(''); await done(); } });
  const remove = useMutation({ mutationFn: (name: string) => api.unshareRecipe(recipeId, name), onSuccess: done });
  const list = shares.data?.shares ?? [];
  return (
    <section className="panel" aria-labelledby="share-title">
      <h2 id="share-title">Share</h2>
      <p className="muted">Friends see this workflow read-only and can copy it to run on their own machine. They never get your machine, runs or images.</p>
      <form className="machine-actions" onSubmit={(e) => { e.preventDefault(); if (username.trim()) add.mutate(); }}>
        <label className="visually-hidden" htmlFor="share-username">Username</label>
        <input id="share-username" placeholder="username" value={username} onChange={(e) => { setUsername(e.target.value); }} autoComplete="off" />
        <button type="submit" disabled={!username.trim() || add.isPending}>Share</button>
      </form>
      {add.isError && <p role="alert" className="form-error">{errorMessage(add.error)}</p>}
      {list.length > 0 && (
        <ul className="share-list">
          {list.map((s) => (
            <li key={s.username}>
              {s.displayName} <span className="muted">({s.username})</span>{' '}
              <button type="button" className="button-quiet" aria-label={`Stop sharing with ${s.username}`} disabled={remove.isPending} onClick={() => { remove.mutate(s.username); }}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      {remove.isError && <p role="alert" className="form-error">{errorMessage(remove.error)}</p>}
    </section>
  );
}
